import { FC, ReactNode, useCallback } from "react";
import { Button, Theme } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { SxProps } from "@mui/system";
import { useFlag } from "@unleash/proxy-client-react";
import { toggleAlertsOpenDrawer } from "../../../../store/alerts/alertsSlice";
import { useAppDispatch, useAppSelector } from "../../../../store/hooks";
import { alertsActiveTabSelector } from "../../../../store/alerts/selectors/alertsActiveTabSelector";
import { initiateCostAnomalyAlertThunk } from "../../../../store/alerts/thunks/cost-anomaly-alerts/initiateCostAnomalyAlertThunk";
import { initiateUtilizationAlertThunk } from "../../../../store/alerts/thunks/utilization-alerts/initiateUtilizationAlertThunk";
import {
  initiateBudgetForCreate,
  setCurrentBudgetData,
} from "../../../../store/budgets/budgetsSlice";
import { resourceExplorerViewsHierarchyDefaultViewSelector } from "@/store/resource-explorer/selectors/views-hierarchy/resourceExplorerViewsHierarchyDefaultViewSelector";

interface CreateAlertsButtonProps {
  children: ReactNode;
  disabled?: boolean;
  variant?: "contained" | "outlined";
  sx?: SxProps<Theme>;
}

export const CreateBudgetOrAlertButton: FC<CreateAlertsButtonProps> = ({
  children,
  disabled,
  variant = "contained",
  sx,
}) => {
  const dispatch = useAppDispatch();
  const alertsActiveTab = useAppSelector(alertsActiveTabSelector);
  const defaultBillingReportId = useAppSelector(
    resourceExplorerViewsHierarchyDefaultViewSelector,
  );
  const enableAzureCommitmentsForAlertsAndWidgets = useFlag(
    "EnableAzureCommitmentsForAlertsAndWidgets",
  );

  const toggleOpenDrawer = useCallback(() => {
    if (alertsActiveTab === "costAnomaly") {
      dispatch(initiateCostAnomalyAlertThunk());
    } else if (alertsActiveTab === "utilization") {
      dispatch(
        initiateUtilizationAlertThunk(
          enableAzureCommitmentsForAlertsAndWidgets,
        ),
      );
    } else {
      dispatch(initiateBudgetForCreate());
      if (defaultBillingReportId) {
        dispatch(setCurrentBudgetData({ viewId: defaultBillingReportId }));
      }
    }

    dispatch(toggleAlertsOpenDrawer());
  }, [
    dispatch,
    alertsActiveTab,
    defaultBillingReportId,
    enableAzureCommitmentsForAlertsAndWidgets,
  ]);

  return (
    <Button
      variant={variant}
      color="primary"
      size="small"
      sx={sx}
      onClick={toggleOpenDrawer}
      disabled={disabled}
      startIcon={<AddIcon />}
    >
      {children}
    </Button>
  );
};
